'use client';

import { c as _c } from "react/compiler-runtime";
import { sendGTMEvent } from '@next/third-parties/google';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';
import { AUTH_CONTINUATION_PARAM, AUTH_CONTINUATIONS, AUTH_RECOVERY_REFRESH_PENDING_KEY, AUTH_RECOVERY_REFRESH_PENDING_TTL_MS, HTC_ACCESS_TOKEN_KEY, OAUTH_CALLBACK_KEYS_TO_STRIP, SEARCH_PARAM_KEYS } from '@/constants/auth';
import { BI_CATEGORY_SIGN_UP, BI_EVENT_WORLD_SIGN_UP } from '@/constants/bi-sdk';
import { useBISdkContext } from '@/context/BISdkContext';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useUAContext } from '@/context/UAContext';
import useGetCommunityToggle from '@/hooks/auth/useGetCommunityToggle';
import { createCredentials, createProfile, createViveportAuth, extractUsernameFromEmail, getHtcAccountId, removeKeysFromSearchParams } from '@/lib/auth';
import { fetchLoginStatusSync, shouldSyncLoginStatus } from '@/lib/auth-login-status-sync';
import { getClientCookie } from '@/lib/client-cookie';
import { enableLoginStatusCheck, getViversePlusSgeRedeemUrl } from '@/lib/configs';
import { getViveCreatorUrl } from '@/lib/url';
import { credentialsSchema } from '@/schemas/auth';
import type { AuthSnapshot } from '@/server/lib/auth-snapshot';
import type { HubsStore } from '@/types/store';
type AuthBootstrapHandlers = {
  /** Reticulum has no resumable session. Cookies were already expired by the route handler. */
  onSignedOut: () => void;
  /** Session is unrecoverable. Cookies are already gone; finish through the logout flow. */
  onInvalidSession: () => void;
};
const isAuthRecoveryRefreshPending = () => {
  try {
    const pendingSince = Number(sessionStorage.getItem(AUTH_RECOVERY_REFRESH_PENDING_KEY));
    const pendingAge = Date.now() - pendingSince;
    if (Number.isFinite(pendingSince) && pendingSince > 0 && pendingAge >= 0) {
      if (pendingAge < AUTH_RECOVERY_REFRESH_PENDING_TTL_MS) return true;
    }
    sessionStorage.removeItem(AUTH_RECOVERY_REFRESH_PENDING_KEY);
    return false;
  } catch {
    return false;
  }
};
const setAuthRecoveryRefreshPending = (pending: boolean) => {
  try {
    if (pending) {
      sessionStorage.setItem(AUTH_RECOVERY_REFRESH_PENDING_KEY, String(Date.now()));
      return;
    }
    sessionStorage.removeItem(AUTH_RECOVERY_REFRESH_PENDING_KEY);
  } catch {
    // The mounted-instance snapshot guard still prevents duplicate bootstrap work.
  }
};
const useAuthBootstrap = (snapshot, t0) => {
  const $ = _c(16);
  const {
    onSignedOut,
    onInvalidSession
  } = t0;
  const {
    isMobileApp
  } = useUAContext();
  const {
    removeStore,
    setStore
  } = useConfigsContext();
  const {
    sendBIReport
  } = useBISdkContext();
  const getCommunityToggle = useGetCommunityToggle();
  const router = useRouter();
  const searchParams = useSearchParams();
  const lastBootstrappedSnapshotKey = useRef(null);
  const didSyncLoginStatus = useRef(false);
  let t1;
  if ($[0] !== getCommunityToggle || $[1] !== isMobileApp || $[2] !== onInvalidSession || $[3] !== onSignedOut || $[4] !== removeStore || $[5] !== router || $[6] !== searchParams || $[7] !== sendBIReport || $[8] !== setStore || $[9] !== snapshot) {
    t1 = async () => {
      const {
        isSignedIn,
        htcAuthKey,
        profile,
        isNewUser,
        isProfileNotSet,
        shouldRemoveStore
      } = snapshot;
      const justSignedIn = Boolean(searchParams.get(SEARCH_PARAM_KEYS.HTC_TOKEN));
      if (isSignedIn) {
        setAuthRecoveryRefreshPending(false);
      }
      const shouldContinueViversePlusSgeRedeem = searchParams.get(AUTH_CONTINUATION_PARAM) === AUTH_CONTINUATIONS.VIVERSE_PLUS_SGE_REDEEM;
      if (shouldRemoveStore) {
        removeStore();
      }
      if (isSignedIn && profile) {
        const oauthCredentials = justSignedIn ? createCredentials(searchParams) : null;
        const credentialsValid = credentialsSchema.safeParse(oauthCredentials).success;
        const accountId = getHtcAccountId(getClientCookie(HTC_ACCESS_TOKEN_KEY)) || extractUsernameFromEmail(oauthCredentials?.email || "");
        if (credentialsValid || process.env.NODE_ENV !== "development") {
          const credentials = {
            htcToken: htcAuthKey
          } as HubsStore['credentials'];
          const communityToggle = await getCommunityToggle(credentials);
          const oauthOverride = credentialsValid && oauthCredentials ? {
            token: oauthCredentials.token,
            expiredAt: oauthCredentials.expiredAt,
            email: oauthCredentials.email
          } : undefined;
          setStore(value => ({
            ...value,
            viveportAuth: {
              ...value.viveportAuth,
              ...createViveportAuth(profile)
            },
            profile: {
              ...value.profile,
              ...createProfile(profile)
            },
            communityToggle,
            credentials: {
              ...value.credentials,
              email: value.credentials?.email ?? "",
              htcToken: htcAuthKey,
              ...(accountId && {
                accountId
              }),
              ...oauthOverride
            }
          }));
        }
      }
      if (enableLoginStatusCheck() && justSignedIn && profile) {
        const emailForAnalytics = searchParams.get(SEARCH_PARAM_KEYS.EMAIL) || "";
        const accountId_0 = extractUsernameFromEmail(emailForAnalytics);
        if (isNewUser) {
          sendBIReport({
            category: BI_CATEGORY_SIGN_UP,
            ev: BI_EVENT_WORLD_SIGN_UP,
            account_id: accountId_0
          });
          sendGTMEvent({
            event: "signup",
            account_id: accountId_0
          });
        } else {
          sendGTMEvent({
            event: "login",
            account_id: accountId_0
          });
        }
      }
      if (isProfileNotSet && justSignedIn) {
        window.location.href = getViveCreatorUrl({
          redirectUrl: shouldContinueViversePlusSgeRedeem ? getViversePlusSgeRedeemUrl() : undefined
        });
        return;
      }
      if (isSignedIn && justSignedIn && shouldContinueViversePlusSgeRedeem) {
        window.location.href = getViversePlusSgeRedeemUrl();
        return;
      }
      if (isMobileApp && profile && "flutterWebview" in window) {
        const flutterWebview = (window as unknown as {
          flutterWebview: {
            postMessage: (payload: string) => void;
          };
        }).flutterWebview;
        flutterWebview.postMessage(JSON.stringify({
          feature: "profile",
          data: {
            headIconUrl: profile.activeAvatar.snapshot,
            displayName: profile.displayName
          }
        }));
      }
      if (justSignedIn) {
        removeKeysFromSearchParams(OAUTH_CALLBACK_KEYS_TO_STRIP);
      }
      if (!isSignedIn && snapshot.visitorIdentity) {
        const {
          displayName,
          avatarId,
          avatarSnapshot,
          avatarGlb,
          isHalfBody
        } = snapshot.visitorIdentity;
        setStore(value_0 => {
          if (!value_0.profile) {
            return value_0;
          }
          return {
            ...value_0,
            profile: {
              ...value_0.profile,
              id: "",
              displayName,
              avatarId,
              avatarSnapshot,
              avatarGlb,
              isHalfBody
            }
          };
        });
      }
      if (!shouldSyncLoginStatus(snapshot) || didSyncLoginStatus.current) {
        return;
      }
      didSyncLoginStatus.current = true;
      const loginStatus = await fetchLoginStatusSync();
      if (loginStatus?.kind === "authenticated") {
        setStore(value_1 => ({
          ...value_1,
          credentials: {
            ...value_1.credentials,
            email: value_1.credentials?.email ?? "",
            htcToken: loginStatus.htcToken
          }
        }));
        if (!snapshot.isSignedIn && !isAuthRecoveryRefreshPending()) {
          setAuthRecoveryRefreshPending(true);
          router.refresh();
        }
        return;
      }
      if (loginStatus?.kind === "signed_out") {
        setAuthRecoveryRefreshPending(false);
        onSignedOut();
        return;
      }
      if (loginStatus?.kind === "invalid_session") {
        setAuthRecoveryRefreshPending(false);
        onInvalidSession();
      }
    };
    $[0] = getCommunityToggle;
    $[1] = isMobileApp;
    $[2] = onInvalidSession;
    $[3] = onSignedOut;
    $[4] = removeStore;
    $[5] = router;
    $[6] = searchParams;
    $[7] = sendBIReport;
    $[8] = setStore;
    $[9] = snapshot;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const bootstrapAuth = t1;
  let t2;
  let t3;
  if ($[11] !== bootstrapAuth || $[12] !== snapshot.htcAuthKey || $[13] !== snapshot.isSignedIn) {
    t2 = () => {
      const snapshotKey = `${snapshot.isSignedIn}:${snapshot.htcAuthKey}`;
      if (lastBootstrappedSnapshotKey.current === snapshotKey) {
        return;
      }
      lastBootstrappedSnapshotKey.current = snapshotKey;
      bootstrapAuth();
    };
    t3 = [bootstrapAuth, snapshot.htcAuthKey, snapshot.isSignedIn];
    $[11] = bootstrapAuth;
    $[12] = snapshot.htcAuthKey;
    $[13] = snapshot.isSignedIn;
    $[14] = t2;
    $[15] = t3;
  } else {
    t2 = $[14];
    t3 = $[15];
  }
  useEffect(t2, t3);
};
export default useAuthBootstrap;